import type { ColorSystemOptions, PaletteColor } from '@mui/material/styles';
import { createPaletteChannel, varAlpha } from 'minimal-shared/utils';

import { themeConfig } from '../themeConfig';
import type { ThemeColorScheme } from '../types';

// ----------------------------------------------------------------------

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../extend-theme-types.d.ts}
 */

// Keys for the palette colors
export type PaletteColorKey = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

// Palette color without additional channels
export type PaletteColorNoChannels = Omit<PaletteColor, 'lighterChannel' | 'darkerChannel'>;

// Extended common colors
export type CommonColorsExtend = {
  whiteChannel: string;
  blackChannel: string;
};

// Extended text colors
export type TypeTextExtend = {
  disabledChannel: string;
};

// Extended background colors
export type TypeBackgroundExtend = {
  neutral: string;
  neutralChannel: string;
};

// Extended palette colors
export type PaletteColorExtend = {
  lighter: string;
  darker: string;
  lighterChannel: string;
  darkerChannel: string;
};

// Extended grey channels
export type GreyExtend = {
  '50Channel': string;
  '100Channel': string;
  '200Channel': string;
  '300Channel': string;
  '400Channel': string;
  '500Channel': string;
  '600Channel': string;
  '700Channel': string;
  '800Channel': string;
  '900Channel': string;
};

// ----------------------------------------------------------------------

// Primary color
export const primary = createPaletteChannel(themeConfig.palette.primary);

// Secondary color
export const secondary = createPaletteChannel(themeConfig.palette.secondary);

// Info color
export const info = createPaletteChannel(themeConfig.palette.info);

// Success color
export const success = createPaletteChannel(themeConfig.palette.success);

// Warning color
export const warning = createPaletteChannel(themeConfig.palette.warning);

// Error color
export const error = createPaletteChannel(themeConfig.palette.error);

// Common color
export const common = createPaletteChannel(themeConfig.palette.common);

// Grey color
export const grey = createPaletteChannel(themeConfig.palette.grey);

// Text color
export const text = {
  light: createPaletteChannel({ primary: '#1F2020', secondary: '#6A6C6C', disabled: '#8D8F8F' }),
  dark: createPaletteChannel({ primary: '#EDEDED', secondary: '#8C8C8C', disabled: '#6E6E6E' }),
};

// Background color
export const background = {
  // White page and cards; `neutral` tints table headers and similar bands.
  light: createPaletteChannel({ paper: '#FFFFFF', default: '#FFFFFF', neutral: '#F2F2F2' }),
  // Dark `neutral` sits one step above BOTH the page and paper, so tinted bands survive inside cards.
  dark: createPaletteChannel({ paper: '#101010', default: '#0A0A0A', neutral: '#161616' }),
};

// Base action color
export const baseAction = {
  disabled: varAlpha(grey['500Channel'], 0.8),
  disabledBackground: varAlpha(grey['500Channel'], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

// Action color
export const action = {
  light: {
    ...baseAction,
    hover: 'rgba(17, 17, 17, 0.04)',
    selected: 'rgba(17, 17, 17, 0.08)',
    focus: 'rgba(17, 17, 17, 0.12)',
    active: '#6A6C6C',
  },
  dark: {
    ...baseAction,
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: 'rgba(255, 255, 255, 0.10)',
    focus: 'rgba(255, 255, 255, 0.14)',
    active: '#8C8C8C',
  },
};

// ----------------------------------------------------------------------

// Base palette
export const basePalette: ColorSystemOptions['palette'] = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  divider: '#E7E7E8',
};

export const palette: Record<ThemeColorScheme, ColorSystemOptions['palette']> = {
  light: {
    ...basePalette,
    text: text.light,
    background: background.light,
    action: action.light,
    divider: '#E7E7E8',
  },
  dark: {
    ...basePalette,
    // Success is the one hue that has to lift in dark mode: #157F49 is too dark to read on #0A0A0A.
    success: createPaletteChannel({ ...themeConfig.palette.success, main: '#4BAE74' }),
    text: text.dark,
    background: background.dark,
    action: action.dark,
    divider: '#1F1F1F',
  },
};
